import React, {useMemo} from 'react';
import {Map, List} from 'immutable';
import {UserId} from 'records/User';
import {UserComment} from 'records/UserComment';
import {CallComment} from '@components/call-card/CallComment';
import './CommentSectionMini.sass';
import {UserAvatar} from '@components/util/UserAvatar';
import {CallTick} from '@components/call-card/CallTick';
import {PlayerProps} from '@components/call-card/Player.utils';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import {buildCommentBuckets, TRACK_HEIGHT} from '@components/call-card/buildCommentBuckets';
import {Seconds} from '@store/@types/util';
import {getStringQueryParam} from 'utils/getQueryParam';

interface CommentSectionMiniProps extends PlayerProps {
  comments: Map<UserId, List<UserComment>>;
  callId: FileUploadId;
  setTimeSeconds: (seconds: Seconds) => void;
  playRecording: () => void;
}

/**
 * Collapsed version of call card comments
 */
export const CommentSectionMini: React.FC<CommentSectionMiniProps> = ({
  comments,
  callId,
  setTimeSeconds,
  playRecording,
  ...playerProps
}) => {
  const {duration, currentTime} = playerProps;

  // Provided by Inbox email links
  const openCommentId = getStringQueryParam(window.location, 'openCommentId');
  const openHighlightId = getStringQueryParam(window.location, 'openHighlightId');

  const {bucketedComments, trackHeight} = useMemo(
    () =>
      buildCommentBuckets(
        comments.toList().flatMap(c => c),
        TRACK_HEIGHT.MINI
      ),
    [comments]
  );

  return (
    <div className="comment-section-mini">
      <div className="track" style={{height: trackHeight}}>
        {bucketedComments.map(({index, comment: c}) => (
          <>
            <CallComment
              comment={c}
              callId={callId}
              mini
              bucketIndex={index}
              defaultOpen={c.id === openCommentId || c.id === openHighlightId}
              canDelete={false}
              setTimeSeconds={setTimeSeconds}
              playRecording={playRecording}
              {...playerProps}
            />
            <CommentAvatar comment={c} duration={playerProps.duration} />
          </>
        ))}
        <CallTick duration={duration} location={currentTime} type="player" />
      </div>
    </div>
  );
};

interface CommentAvatarProps {
  comment: UserComment;
  duration: Seconds;
}

function CommentAvatar({comment, duration}: CommentAvatarProps) {
  return (
    <div className="avatar-wrapper" style={{left: (comment.startTime / duration) * 100 + '%'}}>
      <UserAvatar user={comment.author} />
    </div>
  );
}
